/**
 * Derive the P7 invalidation matrix from two snapshots. No mutation metadata is
 * trusted: validity comes from the inputs themselves.
 */

import { canonicalStringify } from './collab-protocol.js';

const same = (a, b) => canonicalStringify(a ?? null) === canonicalStringify(b ?? null);

function sectionMap(page) {
  return new Map(
    (page?.sections || []).map((section, index) => [
      String(section?.instanceId || `index:${index}`),
      section
    ])
  );
}

export function deriveAuditScope(previous, next) {
  if (!previous || !next) return { kind: 'full', reason: 'unknown-mutation' };

  const pages = new Set();
  const sections = new Set();
  const checks = new Set();
  let allPages = false;
  let globalSeo = false;
  let changed = false;

  const beforePages = new Map((previous.pages || []).map((page) => [String(page?.id), page]));
  const afterPages = new Map((next.pages || []).map((page) => [String(page?.id), page]));
  if (
    beforePages.size !== afterPages.size ||
    [...beforePages.keys()].some((id) => !afterPages.has(id))
  ) {
    changed = true;
    allPages = true;
    checks.add('internal-links');
  }

  for (const [pageId, page] of afterPages) {
    const before = beforePages.get(pageId);
    if (!before) {
      changed = true;
      pages.add(pageId);
      allPages = true;
      checks.add('internal-links');
      continue;
    }
    if (!same(before.slug, page.slug)) {
      changed = true;
      allPages = true;
      checks.add('internal-links');
    }
    if (!same(before.seo, page.seo)) {
      changed = true;
      pages.add(pageId);
      globalSeo = true;
      checks.add('seo');
    }
    const beforeSections = sectionMap(before);
    const afterSections = sectionMap(page);
    if (
      beforeSections.size !== afterSections.size ||
      [...beforeSections.keys()].some((id) => !afterSections.has(id))
    ) {
      changed = true;
      pages.add(pageId);
    }
    for (const [sectionId, section] of afterSections) {
      if (!same(beforeSections.get(sectionId), section)) {
        changed = true;
        pages.add(pageId);
        sections.add(sectionId);
      }
    }
  }

  if (!same(previous.sharedSections, next.sharedSections)) {
    changed = true;
    allPages = true;
    checks.add('shared-sections');
  }
  if (!same(previous.theme, next.theme)) {
    changed = true;
    checks.add('color-contrast');
  }

  // Everything not named by the matrix is deliberately fail-safe. This also
  // covers load, remote snapshot, undo/redo of an unknown legacy path, and a
  // future state field whose audit consequences have not been classified.
  const knownTopLevel = new Set([
    'pages',
    'sharedSections',
    'theme',
    'activePage',
    'updatedAt',
    'cloudVersion',
    'auditAccepts'
  ]);
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
  for (const key of keys) {
    if (knownTopLevel.has(key) || same(previous[key], next[key])) continue;
    return { kind: 'full', reason: `unknown:${key}` };
  }

  if (!changed) return { kind: 'none', pages: [], sections: [], checks: [] };
  return {
    kind: 'incremental',
    pages: allPages ? [...afterPages.keys()].sort() : [...pages].sort(),
    sections: [...sections].sort(),
    checks: [...checks].sort(),
    allPages,
    globalSeo
  };
}
